import { useState, useEffect, useMemo } from 'react'
import { Calculator } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import { PriceCalculatorInputs } from './PriceCalculatorInputs'
import { PriceCalculatorBreakdown } from './PriceCalculatorBreakdown'
import { PriceCalculatorSummary } from './PriceCalculatorSummary'
import { calculateMockPricing, getDefaultInputs } from './price-calculator-mock-data'
import type { PriceCalcInputs } from './price-calculator-types'

interface PriceCalculatorModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function PriceCalculatorModal({ open, onOpenChange }: PriceCalculatorModalProps) {
  const [inputs, setInputs] = useState<PriceCalcInputs>(getDefaultInputs)

  // Reset to defaults when modal opens
  useEffect(() => {
    if (open) setInputs(getDefaultInputs())
  }, [open])

  const result = useMemo(() => calculateMockPricing(inputs), [inputs])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[900px] max-h-[85vh] flex flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calculator className="w-5 h-5" />
            Price Calculator
            <Badge variant="secondary" className="text-[10px] font-normal">
              Demo
            </Badge>
          </DialogTitle>
          <DialogDescription>
            Dynamic pricing estimate for transient stays — rates vary by occupancy, weather, events, and aircraft type.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 sm:grid-cols-[260px_1fr] gap-6 flex-1 min-h-0 overflow-hidden">
          {/* Left: Inputs & legend */}
          <div className="overflow-y-auto pr-2">
            <PriceCalculatorInputs inputs={inputs} onChange={setInputs} />
          </div>

          {/* Right: Daily breakdown table */}
          <div className="min-h-0">
            <PriceCalculatorBreakdown days={result.days} />
          </div>
        </div>

        {/* Summary */}
        <PriceCalculatorSummary result={result} />
      </DialogContent>
    </Dialog>
  )
}
